import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const FOLDER_COLORS = [
    'from-lime-500 to-green-600',
    'from-sky-500 to-blue-600',
    'from-emerald-500 to-teal-600',
    'from-rose-500 to-pink-600',
    'from-amber-500 to-orange-600',
    'from-cyan-500 to-sky-600',
];

const FolderCard = ({ f, onFolderClick, onDelete, onShare, onQuickTest }) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef(null);

    const initial = (f.name || '?').slice(0, 1).toUpperCase();
    const colorClass = f.color || FOLDER_COLORS[Math.abs([...f.name].reduce((a, c) => a + c.charCodeAt(0), 0)) % FOLDER_COLORS.length];
    const total = f.cardCount ?? f.cards?.length ?? 0;
    const mastered = f.masteredCount ?? 0;
    // Only computable once this folder's cards have been loaded into local state (the folder
    // list itself only carries a lightweight cardCount, not full card data) — so this is
    // best-effort: shows once you've opened the folder this session, not before.
    const flaggedCount = Array.isArray(f.cards) ? f.cards.filter(c => c.isFlagged).length : 0;
    const pct = total > 0 ? Math.round((mastered / total) * 100) : 0;

    const dateStr = (() => {
        try {
            const d = f.updatedAt?.toDate ? f.updatedAt.toDate() : new Date(f.updatedAt);
            return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
        } catch { return ''; }
    })();

    useEffect(() => {
        if (!menuOpen) return;
        const handler = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
        };
        document.addEventListener('mousedown', handler);
        document.addEventListener('touchstart', handler, { passive: true });
        return () => {
            document.removeEventListener('mousedown', handler);
            document.removeEventListener('touchstart', handler);
        };
    }, [menuOpen]);

    return (
        <div
            className="relative rounded-2xl border border-slate-100 bg-white shadow-sm hover:border-slate-200 hover:shadow-md dark:border-white/10 dark:bg-slate-900/60 dark:shadow-none dark:hover:border-white/20 dark:hover:bg-slate-900/80 transition-all duration-150 active:scale-[0.985] flex flex-col overflow-hidden"
        >
            {/* Card body — clickable area */}
            <div
                className="flex-1 p-4 cursor-pointer"
                onClick={() => onFolderClick(f)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onFolderClick(f); } }}
            >
                <div className="flex items-start gap-3">
                    {/* Avatar */}
                    <div className={`h-11 w-11 rounded-xl bg-gradient-to-br ${colorClass} flex items-center justify-center shrink-0 shadow-lg`}>
                        <span className="text-white text-sm font-bold">{initial}</span>
                    </div>

                    <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-[15px] text-slate-900 dark:text-white truncate leading-snug">{f.name}</h3>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 flex items-center gap-1.5 flex-wrap">
                            <span>{total} card{total !== 1 ? 's' : ''}</span>
                            {dateStr ? <span>· {dateStr}</span> : null}
                            {flaggedCount > 0 && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 text-amber-500 dark:text-amber-300 px-1.5 py-0.5 text-[10px] font-semibold">
                                    <svg viewBox="0 0 24 24" width="9" height="9" fill="currentColor"><path d="M4 4h16v12l-6-3-6 3V4z" /></svg>
                                    {flaggedCount}
                                </span>
                            )}
                        </p>
                    </div>

                    {/* Overflow menu trigger */}
                    <div className="relative shrink-0" ref={menuRef}>
                        <button
                            onClick={(e) => { e.stopPropagation(); setMenuOpen(o => !o); }}
                            className="min-w-[36px] min-h-[36px] -mr-1 -mt-0.5 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:text-slate-500 dark:hover:text-white dark:hover:bg-white/8 transition"
                            aria-label="More options"
                        >
                            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                                <circle cx="5" cy="12" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="19" cy="12" r="1.5" />
                            </svg>
                        </button>

                        {menuOpen && (
                            <div className="absolute right-0 top-8 z-20 w-44 rounded-xl bg-white border border-slate-100 shadow-xl dark:bg-slate-900 dark:border-white/10 dark:shadow-2xl dark:shadow-black/50 py-1 overflow-hidden">
                                {onShare && (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onShare(f); }}
                                        className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-white/6 transition"
                                    >
                                        <svg className="h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                                        </svg>
                                        Share Deck
                                    </button>
                                )}
                                <div className="mx-3 my-1 border-t border-slate-100 dark:border-white/8" />
                                <button
                                    onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onDelete(f.id); }}
                                    className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-rose-500 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-500/10 transition"
                                >
                                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                    Delete Deck
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Mastery bar */}
                {total > 0 && (
                    <div className="mt-3.5">
                        <div className="flex items-center justify-between mb-1.5">
                            <span className="text-xs text-slate-500">Mastery</span>
                            <span className="text-xs font-medium text-emerald-500 dark:text-emerald-400 tabular-nums">{pct}%</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-slate-100 dark:bg-white/8 overflow-hidden">
                            <div
                                className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-teal-400 transition-all duration-500"
                                style={{ width: `${pct}%` }}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Footer actions */}
            <div className="border-t border-slate-100 dark:border-white/6 flex" onClick={(e) => e.stopPropagation()}>
                <button
                    onClick={() => onFolderClick(f)}
                    className="flex-1 flex items-center justify-center gap-1.5 py-3 text-xs font-medium text-slate-500 hover:text-slate-900 hover:bg-slate-50 dark:text-slate-400 dark:hover:text-white dark:hover:bg-white/4 transition"
                >
                    Open Deck
                    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                    </svg>
                </button>
                {onQuickTest && (
                    <>
                        <div className="w-px bg-slate-100 dark:bg-white/6 self-stretch" />
                        <button
                            onClick={() => onQuickTest(f)}
                            className="flex-1 flex items-center justify-center gap-1.5 py-3 text-xs font-medium text-slate-500 hover:text-slate-900 hover:bg-slate-50 dark:text-slate-400 dark:hover:text-white dark:hover:bg-white/4 transition"
                        >
                            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Exam
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

const SkeletonCard = () => (
    <div className="rounded-2xl border border-slate-100 bg-white dark:border-white/8 dark:bg-slate-900/50 p-4 animate-pulse">
        <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-slate-200 dark:bg-slate-800" />
            <div className="flex-1 space-y-2">
                <div className="h-4 w-32 bg-slate-200 dark:bg-slate-800 rounded" />
                <div className="h-3 w-20 bg-slate-100 dark:bg-slate-800/70 rounded" />
            </div>
        </div>
        <div className="mt-3.5 space-y-1.5">
            <div className="flex justify-between">
                <div className="h-3 w-12 bg-slate-100 dark:bg-slate-800/60 rounded" />
                <div className="h-3 w-8 bg-slate-100 dark:bg-slate-800/60 rounded" />
            </div>
            <div className="h-1.5 rounded-full bg-slate-100 dark:bg-slate-800/60" />
        </div>
    </div>
);

const AppHomeFoldersSection = ({
    sectionClassName = 'lg:max-w-6xl lg:mx-auto',
    searchQuery,
    onSearchChange,
    sortBy,
    isSortOpen,
    onSortOpenToggle,
    onSetSort,
    sortedFolders,
    pagedFolders,
    onCreateFolder,
    onPreviewFolder,
    onDeleteFolder,
    hasMoreFolders = false,
    onLoadMore,
    showLoadingSkeleton = false,
    isLoadingFolders = false,
    onShareFolder,
    onQuickTest,
    onImportCards,
    onExportBackup,
    isExportingBackup = false,
    onRestoreBackup,
    isOnline = true,
}) => {
    const navigate = useNavigate();
    const sortRef = useRef(null);
    const backupRef = useRef(null);
    const [isBackupMenuOpen, setIsBackupMenuOpen] = useState(false);

    const handleFolderClick = (f) => navigate(`/app/deck/${f.id}`);

    // Close sort dropdown on outside click
    useEffect(() => {
        if (!isSortOpen) return;
        const handler = (e) => {
            if (sortRef.current && !sortRef.current.contains(e.target)) onSortOpenToggle(() => false);
        };
        document.addEventListener('mousedown', handler);
        document.addEventListener('touchstart', handler, { passive: true });
        return () => {
            document.removeEventListener('mousedown', handler);
            document.removeEventListener('touchstart', handler);
        };
    }, [isSortOpen, onSortOpenToggle]);

    // Close backup dropdown on outside click
    useEffect(() => {
        if (!isBackupMenuOpen) return;
        const handler = (e) => {
            if (backupRef.current && !backupRef.current.contains(e.target)) setIsBackupMenuOpen(false);
        };
        document.addEventListener('mousedown', handler);
        document.addEventListener('touchstart', handler, { passive: true });
        return () => {
            document.removeEventListener('mousedown', handler);
            document.removeEventListener('touchstart', handler);
        };
    }, [isBackupMenuOpen]);

    const SORT_LABELS = { name: 'Name', updated: 'Updated', created: 'Created' };

    return (
        <section className={`mt-4 sm:mt-6 ${sectionClassName}`}>
            <div className="rounded-2xl border border-slate-100 bg-white shadow-sm dark:border-teal-500/15 dark:bg-slate-900 dark:bg-gradient-to-br dark:from-slate-900/80 dark:to-emerald-500/10 dark:shadow-[0_10px_30px_-10px_rgba(20,184,166,0.3)] p-4 sm:p-6">

                {/* ── Section header ───────────────────────────────────── */}
                <div className="flex flex-col gap-3 sm:gap-4">
                    <div className="flex items-center justify-between gap-3">
                        <div>
                            <h2 className="text-lg sm:text-xl font-semibold text-slate-900 dark:text-white tracking-tight">Your Decks</h2>
                            <p className="text-xs text-slate-500 mt-0.5">
                                {sortedFolders.length} deck{sortedFolders.length !== 1 ? 's' : ''}
                            </p>
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                            {/* Sort */}
                            <div className="relative" ref={sortRef}>
                                <button
                                    type="button"
                                    onClick={() => onSortOpenToggle((v) => !v)}
                                    className="min-h-[36px] inline-flex items-center gap-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-slate-800/70 dark:hover:bg-slate-800 px-3 py-2 text-xs font-medium text-slate-600 dark:text-slate-300 ring-1 ring-slate-200 dark:ring-white/10 transition"
                                >
                                    <svg className="h-3.5 w-3.5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
                                    </svg>
                                    <span className="hidden sm:inline">{SORT_LABELS[sortBy] || 'Sort'}</span>
                                </button>
                                {isSortOpen && (
                                    <div className="absolute right-0 mt-2 w-36 rounded-xl bg-white border border-slate-100 shadow-xl dark:bg-slate-900 dark:border-white/10 dark:shadow-2xl dark:shadow-black/50 py-1 z-10">
                                        {Object.entries(SORT_LABELS).map(([val, label]) => (
                                            <button
                                                key={val}
                                                type="button"
                                                onClick={() => { onSetSort(val); onSortOpenToggle(() => false); }}
                                                className={`w-full text-left px-3.5 py-2.5 text-sm transition ${sortBy === val ? 'text-slate-900 bg-slate-50 dark:text-white dark:bg-white/5' : 'text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-white/5'}`}
                                            >
                                                {label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Import */}
                            {onImportCards && (
                                <button
                                    type="button"
                                    disabled={!isOnline}
                                    onClick={onImportCards}
                                    className="min-h-[36px] inline-flex items-center gap-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-slate-800/70 dark:hover:bg-slate-800 px-3 py-2 text-xs font-medium text-slate-600 dark:text-slate-300 ring-1 ring-slate-200 dark:ring-white/10 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                    </svg>
                                    <span className="hidden sm:inline">Import</span>
                                </button>
                            )}

                            {/* Backup & Restore */}
                            {(onExportBackup || onRestoreBackup) && (
                                <div className="relative" ref={backupRef}>
                                    <button
                                        type="button"
                                        disabled={!isOnline}
                                        onClick={() => setIsBackupMenuOpen((v) => !v)}
                                        className="min-h-[36px] inline-flex items-center gap-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-slate-800/70 dark:hover:bg-slate-800 px-3 py-2 text-xs font-medium text-slate-600 dark:text-slate-300 ring-1 ring-slate-200 dark:ring-white/10 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15V3m0 12l-4-4m4 4l4-4M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2" />
                                        </svg>
                                        <span className="hidden sm:inline">Backup</span>
                                    </button>
                                    {isBackupMenuOpen && (
                                        <div className="absolute right-0 mt-2 w-56 rounded-xl bg-white border border-slate-100 shadow-xl dark:bg-slate-900 dark:border-white/10 dark:shadow-2xl dark:shadow-black/50 py-1 z-10">
                                            {onExportBackup && (
                                                <button
                                                    type="button"
                                                    disabled={isExportingBackup}
                                                    onClick={() => { setIsBackupMenuOpen(false); onExportBackup(); }}
                                                    className="w-full text-left px-3.5 py-2.5 text-sm text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-white/5 transition disabled:opacity-50"
                                                >
                                                    {isExportingBackup ? 'Exporting…' : 'Export Backup (.zip)'}
                                                </button>
                                            )}
                                            {onRestoreBackup && (
                                                <button
                                                    type="button"
                                                    onClick={() => { setIsBackupMenuOpen(false); onRestoreBackup(); }}
                                                    className="w-full text-left px-3.5 py-2.5 text-sm text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-white/5 transition"
                                                >
                                                    Restore from Backup
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Create */}
                            <button
                                type="button"
                                disabled={!isOnline}
                                onClick={onCreateFolder}
                                className="min-h-[36px] inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-teal-600 to-emerald-600 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-white shadow-lg shadow-teal-600/20 hover:shadow-teal-600/30 transition disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                                </svg>
                                <span className="hidden sm:inline">New Deck</span>
                                <span className="sm:hidden">New</span>
                            </button>
                        </div>
                    </div>

                    {/* Search — always visible */}
                    <div className="relative">
                        <input
                            value={searchQuery}
                            onChange={(e) => onSearchChange(e.target.value)}
                            placeholder="Search decks…"
                            className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-teal-500/50 focus:border-teal-400 dark:bg-slate-800/70 dark:border-white/10 dark:text-slate-200 dark:placeholder:text-slate-600 dark:focus:border-teal-500/30 transition"
                        />
                        <svg className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 dark:text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </div>
                </div>

                {/* ── Folder grid ──────────────────────────────────────── */}
                <div className="mt-4 sm:mt-5">
                    {sortedFolders.length === 0 ? (
                        showLoadingSkeleton && isLoadingFolders ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                                {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center rounded-2xl border border-slate-100 bg-slate-50 dark:border-white/6 dark:bg-slate-900/40 p-10 text-center">
                                <div className="flex items-center justify-center h-14 w-14 rounded-2xl bg-teal-50 ring-1 ring-teal-200 dark:bg-teal-500/10 dark:ring-teal-500/25 mb-4">
                                    <svg viewBox="0 0 24 24" width="26" height="26" className="text-teal-400 dark:text-teal-300" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M3 7h5l2 3h11v7a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                                    </svg>
                                </div>
                                <p className="text-base font-medium text-slate-900 dark:text-white tracking-tight">No decks yet</p>
                                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-xs">Create your first deck to start organizing your study sets.</p>
                                <button
                                    type="button"
                                    onClick={onCreateFolder}
                                    className="mt-5 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-teal-600 to-emerald-600 px-5 py-2.5 text-sm font-medium text-white shadow-lg shadow-teal-600/20"
                                >
                                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                                    </svg>
                                    Create First Deck
                                </button>
                            </div>
                        )
                    ) : (
                        <>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                                {pagedFolders.map((f) => (
                                    <FolderCard
                                        key={f.id}
                                        f={f}
                                        onFolderClick={handleFolderClick}
                                        onDelete={onDeleteFolder}
                                        onShare={onShareFolder}
                                        onQuickTest={onQuickTest}
                                    />
                                ))}
                            </div>

                            {hasMoreFolders && onLoadMore && (
                                <div className="mt-5 flex justify-center">
                                    <button
                                        type="button"
                                        onClick={onLoadMore}
                                        className="inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm text-slate-600 bg-slate-100 hover:bg-slate-200 dark:text-slate-300 dark:bg-slate-800/70 dark:hover:bg-slate-800 ring-1 ring-slate-200 dark:ring-white/10 transition"
                                    >
                                        Load more decks
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </section>
    );
};

export default AppHomeFoldersSection;
